import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Pencil, Plus, Trash2 } from 'lucide-react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { ApiError } from '@/core/api';
import { formatMoney } from '@/lib/money';
import { useSession } from '@/stores/session';
import { RecruitmentTabs } from '../components/RecruitmentTabs';
import { OFFER_STATUS_LABEL, type OfferStatus } from '../constants';
import { useCandidates, useDeleteOffer, useOffers } from '../queries';
import type { Offer } from '../types';
import { candidateName } from '../types';

const STATUS_VARIANT: Record<OfferStatus, 'secondary' | 'outline' | 'destructive'> = {
  draft: 'outline',
  sent: 'secondary',
  accepted: 'secondary',
  declined: 'destructive',
};

const MONEY_VISIBLE_ROLES = new Set(['founder', 'super_admin', 'accountant', 'hr']);

export function OffersPage() {
  const navigate = useNavigate();
  const role = useSession((s) => s.role);
  const canSeeMoney = role ? MONEY_VISIBLE_ROLES.has(role) : false;

  const { data, isPending, isError, error, isFetching } = useOffers();
  const { data: candidatesData } = useCandidates();
  const remove = useDeleteOffer();
  const [toDelete, setToDelete] = useState<Offer | null>(null);

  const candidates = useMemo(() => candidatesData?.results ?? [], [candidatesData]);
  const candidateLabel = useMemo(() => {
    const map = new Map<number, string>();
    for (const c of candidates) map.set(c.id, candidateName(c));
    return map;
  }, [candidates]);

  const offers = data?.results ?? [];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold tracking-tight">Recruitment</h1>
        {canSeeMoney ? (
          <Button asChild>
            <Link to="/hrm/recruitment/offers/new">
              <Plus className="size-4" /> New offer
            </Link>
          </Button>
        ) : null}
      </div>

      <RecruitmentTabs />

      {isError ? (
        <p className="text-destructive text-sm">
          {(error as unknown as ApiError)?.message ?? 'Failed to load offers.'}
        </p>
      ) : (
        <div className="rounded-md border" aria-busy={isFetching}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Candidate</TableHead>
                <TableHead className="text-right">Offered</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Sent</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {isPending ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={5}>
                      <Skeleton className="h-6 w-full" />
                    </TableCell>
                  </TableRow>
                ))
              ) : offers.length ? (
                offers.map((o) => (
                  <TableRow
                    key={o.id}
                    className="cursor-pointer"
                    onClick={() => navigate(`/hrm/recruitment/offers/${o.public_id}`)}
                  >
                    <TableCell className="font-medium">
                      {candidateLabel.get(o.candidate) ?? `#${o.candidate}`}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {o.amount && o.currency
                        ? formatMoney(o.amount, o.currency)
                        : canSeeMoney
                          ? '—'
                          : <Badge variant="outline" className="text-[10px]">Restricted</Badge>}
                    </TableCell>
                    <TableCell>
                      <Badge variant={STATUS_VARIANT[o.status]}>{OFFER_STATUS_LABEL[o.status]}</Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground whitespace-nowrap">
                      {o.sent_at ? new Date(o.sent_at).toLocaleDateString() : '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      {canSeeMoney ? (
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            size="icon"
                            variant="ghost"
                            aria-label="Edit offer"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/hrm/recruitment/offers/${o.public_id}/edit`);
                            }}
                          >
                            <Pencil className="size-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            aria-label="Delete offer"
                            onClick={(e) => {
                              e.stopPropagation();
                              setToDelete(o);
                            }}
                          >
                            <Trash2 className="size-4" />
                          </Button>
                        </div>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} className="text-muted-foreground text-center">
                    No offers yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}

      <AlertDialog open={Boolean(toDelete)} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete offer?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (toDelete) remove.mutate(toDelete.id);
                setToDelete(null);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
